import { useEffect, useCallback, useRef, useState } from 'react';
import { 
  getClientsListState, 
  saveClientsListState, 
  getClientsScrollPosition,
  saveClientsScrollPosition
} from '@/utils/localStorage';

interface UseScrollRestorationOptions {
  items: any[];
  enabled?: boolean;
  debounceMs?: number;
  restoreDelay?: number;
  organizationId?: string;
}


export const useScrollRestoration = ({
  items,
  enabled = true,
  debounceMs = 100,
  restoreDelay = 300,
  organizationId
}: UseScrollRestorationOptions) => {
  
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();
  const restorationTimeoutRef = useRef<NodeJS.Timeout>();
  const isRestoringRef = useRef(false);

  // Debounced scroll position saving
  const debouncedSaveScrollPosition = useCallback((position: number) => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    scrollTimeoutRef.current = setTimeout(() => {
      try {
        if (!isRestoringRef.current) {
          saveClientsScrollPosition(position, organizationId);
          console.log('💾 Scroll position saved:', position);
        }
      } catch (error) {
        console.error('❌ Error saving scroll position:', error);
      }
    }, debounceMs);
  }, [debounceMs, organizationId]);

  // Handle scroll restoration with mobile-specific considerations
  useEffect(() => {
    if (!enabled || items.length === 0) return;

    const performScrollRestoration = async () => {
      try {
        const savedState = getClientsListState();
        console.log('🔍 Checking scroll restoration state:', savedState);
        
        // Only restore scroll if explicitly requested (coming back from client details)
        if (!savedState.shouldRestoreScroll) {
          console.log('ℹ️ Skipping scroll restoration - shouldRestoreScroll flag is:', savedState.shouldRestoreScroll);
          return;
        }
        
        console.log('🎯 Starting scroll restoration for mobile/desktop');
        isRestoringRef.current = true;

        if (savedState.lastViewedClientId) {
          console.log('👀 Looking for client element:', savedState.lastViewedClientId);
          
          // Give DOM time to render before searching for element
          await new Promise(resolve => setTimeout(resolve, 100));
          
          const clientElement = document.querySelector(`[data-client-id="${savedState.lastViewedClientId}"]`);
          
          if (clientElement) {
            console.log('✅ Found client element, scrolling...');
            
            // Use more reliable scroll positioning
            const elementRect = clientElement.getBoundingClientRect();
            const elementTop = elementRect.top + window.scrollY;
            const offset = window.innerHeight * 0.25; // 25% from top
            const targetScroll = Math.max(0, elementTop - offset);
            
            console.log('📍 Scrolling to position:', targetScroll);
            
            // Use immediate scroll for mobile reliability
            window.scrollTo({ 
              top: targetScroll, 
              behavior: 'smooth' 
            });
            
            console.log('✅ Successfully scrolled to client:', savedState.lastViewedClientId);
            
            // Clear the restoration flags after successful restoration
            const newState = { ...savedState };
            delete newState.lastViewedClientId;
            delete newState.shouldRestoreScroll;
            saveClientsListState(newState, organizationId);
            console.log('🧹 Cleared restoration flags');
            
          } else {
            console.log('⚠️ Client element not found, trying scroll position fallback');
            const scrollPosition = getClientsScrollPosition(organizationId);
            if (scrollPosition > 0) {
              console.log('📍 Fallback scrolling to position:', scrollPosition);
              window.scrollTo({ 
                top: scrollPosition, 
                behavior: 'smooth' 
              });
              console.log('✅ Restored to fallback scroll position:', scrollPosition);
            }
          }
        } else {
          console.log('ℹ️ No client ID found, checking for scroll position restore');
          const scrollPosition = getClientsScrollPosition(organizationId);
          if (scrollPosition > 0) {
            console.log('📍 Scrolling to saved position:', scrollPosition);
            window.scrollTo({ 
              top: scrollPosition, 
              behavior: 'smooth' 
            });
            console.log('✅ Restored scroll position (no client ID):', scrollPosition);
          }
        }

        // Clear the shouldRestoreScroll flag after restoration completes
        setTimeout(() => {
          const currentState = getClientsListState();
          if (currentState.shouldRestoreScroll) {
            console.log('🧹 Final cleanup of restoration flags');
            const newState = { ...currentState };
            delete newState.shouldRestoreScroll;
            saveClientsListState(newState);
          }
          isRestoringRef.current = false;
        }, 1000);

      } catch (error) {
        console.error('❌ Scroll restoration failed:', error);
        isRestoringRef.current = false;
      }
    };

    if (restorationTimeoutRef.current) {
      clearTimeout(restorationTimeoutRef.current);
    }

    // Start restoration after a short delay to ensure DOM is ready
    restorationTimeoutRef.current = setTimeout(performScrollRestoration, 200);

    return () => {
      if (restorationTimeoutRef.current) {
        clearTimeout(restorationTimeoutRef.current);
      }
    };
  }, [items.length, enabled, restoreDelay, organizationId]);

  // Handle scroll events with debouncing
  useEffect(() => {
    if (!enabled) return;

    const handleScroll = () => {
      debouncedSaveScrollPosition(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [enabled, debouncedSaveScrollPosition]);

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      console.log('🧹 Cleaning up scroll restoration hook');
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (restorationTimeoutRef.current) {
        clearTimeout(restorationTimeoutRef.current);
      }
    };
  }, []);

  const saveClientNavigation = useCallback((clientId: string) => {
    try {
      console.log('🚀 Saving client navigation for ID:', clientId);
      
      // Immediately save current scroll position
      const currentScrollPosition = window.scrollY;
      saveClientsScrollPosition(currentScrollPosition);
      console.log('💾 Saved current scroll position:', currentScrollPosition);
      
      // Save client ID and flag for restoration with force write
      const currentState = getClientsListState();
      const newState = {
        ...currentState,
        lastViewedClientId: clientId,
        shouldRestoreScroll: true,
      };
      
      console.log('📝 About to save state:', newState);
      saveClientsListState(newState);
      
      // Verify the state was saved correctly
      const verifyState = getClientsListState();
      console.log('✅ Verified saved state:', verifyState);
      
      if (!verifyState.shouldRestoreScroll || verifyState.lastViewedClientId !== clientId) {
        console.log('⚠️ State verification failed, retrying...');
        saveClientsListState(newState);
        const retryVerify = getClientsListState();
        console.log('🔄 Retry verification:', retryVerify);
      }
      
    } catch (error) {
      console.error('❌ Error saving client navigation:', error);
    }
  }, []);

  return {
    saveClientNavigation
  };
};